import { useState, useCallback } from "react";
import clsx from "clsx";
import { Button } from "./Button";
import {
  validateImport,
  importItemToCreateData,
} from "../services/importService";
import type {
  ImportValidationResult,
  POIPlannerExport,
} from "@poi-planner/shared";

interface POIImportFormProps {
  onImport: (
    pois: ReturnType<typeof importItemToCreateData>[],
    takeout?: POIPlannerExport,
  ) => Promise<void> | void;
  onCancel: () => void;
}

export function POIImportForm({ onImport, onCancel }: POIImportFormProps) {
  const [jsonInput, setJsonInput] = useState("");
  const [validation, setValidation] = useState<ImportValidationResult | null>(
    null,
  );
  const [selectedPois, setSelectedPois] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  // Parse and validate on input change
  const handleInputChange = useCallback((value: string) => {
    setJsonInput(value);
    setImportError(null);

    if (!value.trim()) {
      setValidation(null);
      setSelectedPois(new Set());
      return;
    }

    const result = validateImport(value);
    setValidation(result);

    // Auto-select all valid POIs
    if (result.valid) {
      setSelectedPois(new Set(result.pois.map((_, i) => i)));
    } else {
      setSelectedPois(new Set());
    }
  }, []);

  // Handle file drop
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file && file.type === "application/json") {
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target?.result as string;
          handleInputChange(content);
        };
        reader.readAsText(file);
      }
    },
    [handleInputChange],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Toggle POI selection
  const togglePoi = (index: number) => {
    setSelectedPois((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Toggle all
  const toggleAll = () => {
    if (!validation) return;
    if (selectedPois.size === validation.pois.length) {
      setSelectedPois(new Set());
    } else {
      setSelectedPois(new Set(validation.pois.map((_, i) => i)));
    }
  };

  // Import selected POIs
  const handleImport = async () => {
    if (!validation || selectedPois.size === 0) return;

    setImporting(true);
    setImportError(null);

    const poisToImport = validation.pois
      .filter((_, i) => selectedPois.has(i))
      .map(importItemToCreateData);

    try {
      await onImport(poisToImport, validation.takeout);
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const hasValidPois = validation?.valid && validation.pois.length > 0;
  const hasSelection = selectedPois.size > 0;

  return (
    <div className="flex flex-col gap-4">
      {/* JSON input area */}
      <div onDrop={handleDrop} onDragOver={handleDragOver} className="relative">
        <textarea
          value={jsonInput}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={`Paste JSON or drop a file here...

Supported formats:
• Single POI: { "name": "Tokyo Tower", "lat": 35.65, "lng": 139.74 }
• Array of POIs: [{ "name": "..." }, { "name": "..." }]
• PlotPlanGo export file`}
          className="w-full h-40 px-3 py-2 text-sm font-mono bg-subtle border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent resize-none"
        />
        {!jsonInput && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-tertiary">
              <span className="material-symbols-outlined text-3xl mb-1 block">
                upload_file
              </span>
              <span className="text-xs">Drop JSON file here</span>
            </div>
          </div>
        )}
      </div>

      {/* Validation feedback */}
      {validation && (
        <div className="space-y-3">
          {/* Errors */}
          {validation.errors.length > 0 && (
            <div className="bg-error-bg border border-error/25 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-error mb-1">
                <span className="material-symbols-outlined text-base">
                  error
                </span>
                {validation.errors.length} error
                {validation.errors.length !== 1 ? "s" : ""}
              </div>
              <ul className="text-xs text-error space-y-0.5 pl-6 list-disc">
                {validation.errors.slice(0, 5).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {validation.errors.length > 5 && (
                  <li>...and {validation.errors.length - 5} more</li>
                )}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {validation.warnings.length > 0 && (
            <div className="bg-warning-bg border border-warning/25 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-warning mb-1">
                <span className="material-symbols-outlined text-base">
                  warning
                </span>
                {validation.warnings.length} warning
                {validation.warnings.length !== 1 ? "s" : ""}
              </div>
              <ul className="text-xs text-warning space-y-0.5 pl-6 list-disc">
                {validation.warnings.slice(0, 3).map((warn, i) => (
                  <li key={i}>{warn}</li>
                ))}
                {validation.warnings.length > 3 && (
                  <li>...and {validation.warnings.length - 3} more</li>
                )}
              </ul>
            </div>
          )}

          {/* POI preview list */}
          {hasValidPois && (
            <div className="border border-border rounded-lg overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-3 py-2 bg-subtle border-b border-border">
                <div className="flex items-center gap-2">
                  {!validation.takeout && (
                    <button
                      onClick={toggleAll}
                      className="p-0.5 hover:bg-border rounded transition-colors"
                    >
                      <span className="material-symbols-outlined text-base text-secondary">
                        {selectedPois.size === validation.pois.length
                          ? "check_box"
                          : selectedPois.size > 0
                            ? "indeterminate_check_box"
                            : "check_box_outline_blank"}
                      </span>
                    </button>
                  )}
                  <span className="text-xs font-medium text-secondary">
                    {validation.pois.length} POI
                    {validation.pois.length !== 1 ? "s" : ""} found
                  </span>
                </div>
                {validation.takeout ? (
                  <span className="text-xs text-tertiary">
                    Full takeout — imports everything
                  </span>
                ) : (
                  <span className="text-xs text-tertiary">
                    {selectedPois.size} selected
                  </span>
                )}
              </div>

              {/* POI list */}
              <div className="max-h-48 overflow-y-auto divide-y divide-border">
                {validation.pois.map((poi, index) => (
                  <label
                    key={index}
                    className={clsx(
                      "flex items-center gap-3 px-3 py-2 transition-colors",
                      validation.takeout ? "cursor-default" : "cursor-pointer",
                      selectedPois.has(index)
                        ? "bg-slate-50"
                        : "hover:bg-slate-50",
                    )}
                  >
                    {!validation.takeout && (
                      <input
                        type="checkbox"
                        checked={selectedPois.has(index)}
                        onChange={() => togglePoi(index)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-accent"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-primary truncate">
                        {poi.name}
                      </div>
                      <div className="text-xs text-tertiary">
                        {poi.lat && poi.lng ? (
                          <span>
                            {poi.lat.toFixed(4)}, {poi.lng.toFixed(4)}
                          </span>
                        ) : (
                          <span className="italic">No coordinates</span>
                        )}
                        {poi.tags && poi.tags.length > 0 && (
                          <span className="ml-2">
                            {poi.tags.slice(0, 3).join(", ")}
                            {poi.tags.length > 3 && ` +${poi.tags.length - 3}`}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import error */}
      {importError && (
        <div className="bg-error-bg border border-error/25 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-error">
            <span className="material-symbols-outlined text-base">error</span>
            {importError}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
        <Button onClick={onCancel} variant="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          disabled={!hasSelection || importing}
          variant="primary"
          loading={importing}
        >
          {importing
            ? "Importing..."
            : validation?.takeout
              ? "Import takeout"
              : `Import ${selectedPois.size} POI${selectedPois.size !== 1 ? "s" : ""}`}
        </Button>
      </div>
    </div>
  );
}
